import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCloud } from '../context/CloudContext';
import { useAuth } from '../context/AuthContext';
import SearchBar from '../components/SearchBar';
import Icon from '../components/Icon';
import { ICONS } from '../lib/icons';

// titleCaseRole turns a role slug (e.g. "org-admin") into a display label.
function titleCaseRole(role) {
  if (!role) return '';
  return role
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

// The app bar: a full-width dark navigation bar echoing the AWS console —
// brand, global search, utility icons, region/provider scope, and account.
// It stays dark regardless of the page theme.
export default function TopBar() {
  const { providers, activeProvider, setActiveProvider } = useCloud();
  const { user, logout, allows } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the account menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const onLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="appbar">
      {/* Console home is grantable, so the brand falls back to All services for
          an account whose policies do not include it. */}
      <Link
        to={allows('home') ? '/home' : '/services'}
        className="appbar__brand"
        aria-label="Toffs Cloud home"
      >
        <img
          className="appbar__logo-img"
          src={`${process.env.PUBLIC_URL}/assets/toffs_logo.png`}
          alt="Toffs Cloud"
        />
      </Link>

      <div className="appbar__divider" />

      <SearchBar />

      <div className="appbar__right">
        <button className="appbar__icon" title="CloudShell" aria-label="CloudShell">
          <Icon name={ICONS.cloudShell} />
        </button>
        <button
          className="appbar__icon"
          title="Notifications"
          aria-label="Notifications"
        >
          <Icon name={ICONS.notifications} />
          <span className="appbar__icon-dot" />
        </button>
        <button className="appbar__icon" title="Help" aria-label="Help">
          <Icon name={ICONS.help} />
        </button>
        <button className="appbar__icon" title="Settings" aria-label="Settings">
          <Icon name={ICONS.settings} />
        </button>

        <div className="appbar__divider" />

        <select
          className="appbar__region"
          value={activeProvider}
          onChange={(e) => setActiveProvider(e.target.value)}
          aria-label="Provider scope"
          title="Provider scope"
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.shortName}
            </option>
          ))}
        </select>

        <div className="appbar__accountwrap" ref={menuRef}>
          <button
            className="appbar__account"
            aria-label="Account"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="appbar__account-line">
              {user?.name || 'Account'}
              <Icon name={ICONS.caretDown} className="appbar__account-caret" />
            </span>
            <span className="appbar__account-sub">
              {titleCaseRole(user?.role) || user?.email}
            </span>
          </button>

          {menuOpen && (
            <div className="appbar__menu" role="menu">
              <div className="appbar__menu-head">
                <div className="appbar__menu-name">{user?.name}</div>
                <div className="appbar__menu-mail">{user?.email}</div>
                {user?.role ? (
                  <div className="appbar__menu-role">{titleCaseRole(user.role)}</div>
                ) : null}
              </div>
              <button className="appbar__menu-item" role="menuitem" onClick={onLogout}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
